import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import {
    Box,
    Card,
    TextField,
    Typography,
    Button,
    Alert,
    CircularProgress,
    InputAdornment,
    IconButton,
    Link,
} from "@mui/material";
import { 
    Visibility, 
    VisibilityOff, 
    Email, 
    Lock, 
    Person,
    Phone 
} from "@mui/icons-material";
import { setVendorCredentials } from "../../features/admin/authSlice.ts";

const VendorLogin = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    
    const [currentView, setCurrentView] = useState("login");
    const [showPassword, setShowPassword] = useState(false);
    const [showConfirmPassword, setShowConfirmPassword] = useState(false);
    const [errorMsg, setErrorMsg] = useState("");
    const [successMsg, setSuccessMsg] = useState("");
    const [loading, setLoading] = useState(false);

    const [loginEmail, setLoginEmail] = useState("");
    const [loginPassword, setLoginPassword] = useState("");

    const [regFirstName, setRegFirstName] = useState("");
    const [regLastName, setRegLastName] = useState("");
    const [regEmail, setRegEmail] = useState("");
    const [regContact, setRegContact] = useState("");
    const [regPassword, setRegPassword] = useState("");
    const [regConfirmPassword, setRegConfirmPassword] = useState("");

    const [otpEmail, setOtpEmail] = useState("");
    const [otp, setOtp] = useState("");
    const [showOtpInput, setShowOtpInput] = useState(false);

    const [resetEmail, setResetEmail] = useState("");
    const [resetOtp, setResetOtp] = useState("");
    const [newPassword, setNewPassword] = useState("");
    const [confirmNewPassword, setConfirmNewPassword] = useState("");
    const [showResetOtpInput, setShowResetOtpInput] = useState(false);
    const [showPasswordFields, setShowPasswordFields] = useState(false);

    const BASE_URL = (process.env.REACT_APP_SERVER_ENDPOINT || '').replace(/\/$/, '');
    // Backend mounts vendor authentication under /user.
    // Keep this safe if the environment already contains /user.
    const USER_API_URL = BASE_URL.endsWith('/user') ? BASE_URL : `${BASE_URL}/user`;
    const AUTH_KEY = "7b4e1fd03f57885c1ff900ad76ea3810bb7160c7157a4c31cf2de3";

    const validateEmail = (email) => {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        return emailRegex.test(email);
    };

    const validatePhone = (phone) => {
        const phoneRegex = /^[0-9]{10}$/;
        return phoneRegex.test(phone);
    };

    const validateVendorPassword = (password) => {
        return /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z0-9]).{8,}$/.test(password);
    };
const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setErrorMsg("");
    setSuccessMsg("");

    if (!loginEmail.trim()) {
        setErrorMsg("Email is required");
        setLoading(false);
        return;
    }

    if (!validateEmail(loginEmail)) {
        setErrorMsg("Please enter a valid email address");
        setLoading(false);
        return;
    }

    if (!loginPassword.trim()) {
        setErrorMsg("Password is required");
        setLoading(false);
        return;
    }

    try {
        const response = await fetch(`${USER_API_URL}/vendorlogin`, {
            method: "POST",
            headers: {
                "Content-Type": "application/x-www-form-urlencoded",
                "Accept": "application/json",
                "authkey": AUTH_KEY,
            },
            body: new URLSearchParams({
                user_email: loginEmail.trim(),
                user_pass: loginPassword,
            }),
        });

        const result = await response.json();

        const payload = result?.data && typeof result.data === "object" ? result.data : result;

        if (!result.error && payload.token) {
            // Debug: Log the entire response
            console.log("=== FULL RESPONSE ===");
            console.log(JSON.stringify(result, null, 2));
            console.log("Result object:", result);
            console.log("Type of result:", typeof result);
            console.log("Is array?:", Array.isArray(result));
            if (result.data) console.log("result.data:", result.data);
            if (result.user) console.log("result.user:", result.user);
            console.log("=====================");
            
            // Extract vendor_id - try multiple possible locations
            let vendorId = payload.vendor_id || payload.vendorId || payload.VendorId || payload.id;
            
            // If not found at root level, check other possible locations
            if (!vendorId) {
                vendorId = payload.vendor_id || payload.VendorId || payload.vendorId || payload.id;
            }
            
            const userName = payload.user_name || payload.userName || payload.name || `Vendor User`;

            // Allow login even if vendorId is missing (will use as fallback)
            if (!vendorId) {
                console.warn("Warning: vendor_id not found in response");
                // Don't fail here - use a temporary ID and let backend handle it
                setErrorMsg("Vendor ID was not returned by the server. Please contact the administrator.");
                setLoading(false);
                return;
            }

            // Clear any existing admin role first
            localStorage.removeItem("admin_token");
            localStorage.removeItem("user_role");

            // Store vendor credentials in localStorage
            localStorage.setItem("vendor_token", payload.token);
            localStorage.setItem("user_role", "vendor");
            localStorage.setItem("user_email", loginEmail.trim());
            localStorage.setItem("vendor_id", vendorId.toString());
            localStorage.setItem("user_name", userName);

            // Dispatch Redux action to store credentials
            dispatch(setVendorCredentials({
                token: payload.token,
                email: loginEmail.trim(),
                vendorId: vendorId,
                userName: userName,
                role: "vendor"
            }));

            setSuccessMsg("Login successful! Redirecting to Product Management...");

            // Redirect to Product Management page
            setTimeout(() => {
                navigate("/productManagement");
            }, 1500);
        } else {
            setErrorMsg(result.message || "Invalid credentials");
        }
    } catch (error) {
        setErrorMsg("Network error: Unable to connect to server");
    } finally {
        setLoading(false);
    } 
};
    const handleRegistration = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!regFirstName.trim()) {
            setErrorMsg("First name is required");
            setLoading(false);
            return;
        }

        if (!regLastName.trim()) {
            setErrorMsg("Last name is required");
            setLoading(false);
            return;
        }

        if (!validateEmail(regEmail)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        if (!validatePhone(regContact)) {
            setErrorMsg("Please enter a valid 10-digit contact number");
            setLoading(false);
            return;

        }

        if (!validateVendorPassword(regPassword)) {
            setErrorMsg("Password must be at least 8 characters and include uppercase, lowercase, number and special character");
            setLoading(false);
            return;
        }

        if (regPassword !== regConfirmPassword) {
            setErrorMsg("Passwords do not match");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorregister`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    first_name: regFirstName.trim(),
                    last_name: regLastName.trim(),
                    user_email: regEmail.trim(),
                    user_contact: regContact.trim(),
                    user_pass: regPassword,
                    confirm_password: regConfirmPassword,
                }),
            });

            const result = await response.json();
            const payload = result?.data && typeof result.data === "object" ? result.data : result;

            if (!result.error) {
                setSuccessMsg(payload.message || result.message || "Registration successful! OTP sent to email.");
                setOtpEmail(regEmail);
                setShowOtpInput(true);
                setRegFirstName("");
                setRegLastName("");
                setRegEmail("");
                setRegContact("");
                setRegPassword("");
                setRegConfirmPassword("");
            } else {
                setErrorMsg(result.message || "Registration failed");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleVerifyOtp = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!otp.trim()) {
            setErrorMsg("OTP is required");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorverify-otp`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: otpEmail,
                    otp: otp,
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "OTP verified successfully!");
                setTimeout(() => {
                    setShowOtpInput(false);
                    setOtp("");
                    setCurrentView("login");
                }, 2000);
            } else {
                setErrorMsg(result.message || "Invalid OTP");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleForgotPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!validateEmail(resetEmail)) {
            setErrorMsg("Please enter a valid email address");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorforgot-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: resetEmail.trim(),
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "OTP sent to your email");
                setShowResetOtpInput(true);
            } else {
                setErrorMsg(result.message || "Failed to send OTP");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    const handleResetPassword = async (e) => {
        e.preventDefault();
        setLoading(true);
        setErrorMsg("");
        setSuccessMsg("");

        if (!resetOtp.trim()) {
            setErrorMsg("OTP is required");
            setLoading(false);
            return;
        }

        if (!showPasswordFields) {
            setShowPasswordFields(true);
            setLoading(false);
            return;
        }

        if (!validateVendorPassword(newPassword)) {
            setErrorMsg("Password must be at least 8 characters and include uppercase, lowercase, number and special character");
            setLoading(false);
            return;
        }

        if (newPassword !== confirmNewPassword) {
            setErrorMsg("Passwords do not match");
            setLoading(false);
            return;
        }

        try {
            const response = await fetch(`${USER_API_URL}/vendorreset-password`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/x-www-form-urlencoded",
                    "Accept": "application/json",
                    "authkey": AUTH_KEY,
                },
                body: new URLSearchParams({
                    user_email: resetEmail.trim(),
                    otp: resetOtp,
                    new_password: newPassword,
                    confirm_password: confirmNewPassword,
                }),
            });

            const result = await response.json();

            if (!result.error) {
                setSuccessMsg(result.message || "Password reset successful!");
                setTimeout(() => {
                    setResetEmail("");
                    setResetOtp("");
                    setNewPassword("");
                    setConfirmNewPassword("");
                    setShowResetOtpInput(false);
                    setShowPasswordFields(false);
                    setCurrentView("login");
                }, 2000);
            } else {
                setErrorMsg(result.message || "Password reset failed");
            }
        } catch (error) {
            setErrorMsg("Network error: Unable to connect to server");
        } finally {
            setLoading(false);
        }
    };

    return (
        <Box
            sx={{
                minHeight: "100vh",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
                padding: 2,
            }}
        >
            <Card
                sx={{
                    width: 450,
                    padding: 4,
                    boxShadow: "0 8px 32px rgba(0,0,0,0.1)",
                    borderRadius: 3,
                    backgroundColor: "#ffffff",
                }}
            >
                <Typography
                    variant="h4"
                    align="center"
                    gutterBottom
                    sx={{
                        fontWeight: "bold",
                        mb: 1,
                        color: "#333",
                    }}
                >
                    {currentView === "login" && "Vendor Login"}
                    {currentView === "register" && (showOtpInput ? "Verify OTP" : "Sign Up")}
                    {currentView === "forgot" && "Forgot Password"}
                </Typography>

                <Typography
                    variant="body2"
                    align="center"
                    sx={{ mb: 3, color: "#666" }}
                >
                    {currentView === "login" && ""}
                    {currentView === "register" && (showOtpInput ? "Verify your email address" : "Create your vendor account")}
                    {currentView === "forgot" && "Reset your password"}
                </Typography>

                {successMsg && (
                    <Alert severity="success" sx={{ mb: 2 }}>
                        {successMsg}
                    </Alert>
                )}

                {errorMsg && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {errorMsg}
                    </Alert>
                )}

                {currentView === "login" && (
                    <form onSubmit={handleLogin}>
                        <TextField
                            placeholder="Type your username"
                            type="email"
                            fullWidth
                            value={loginEmail}
                            onChange={(e) => setLoginEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Type your password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={loginPassword}
                            onChange={(e) => setLoginPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Box sx={{ textAlign: "right", mb: 3 }}>
                            <Link
                                component="button"
                                type="button"
                                variant="body2"
                                onClick={() => setCurrentView("forgot")}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Forgot password?
                            </Link>
                        </Box>

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Signing In..
                                </>
                            ) : (
                                "LOGIN"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Don't have an account?{" "}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => setCurrentView("register")}
                                    sx={{ 
                                        textDecoration: "none",
                                        color: "#667eea",
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    SIGN UP
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                )}

                {currentView === "register" && !showOtpInput && (
                    <form onSubmit={handleRegistration}>
                        <TextField
                            placeholder="First Name"
                            fullWidth
                            value={regFirstName}
                            onChange={(e) => setRegFirstName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Last Name"
                            fullWidth
                            value={regLastName}
                            onChange={(e) => setRegLastName(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Person sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Email Address"
                            type="email"
                            fullWidth
                            value={regEmail}
                            onChange={(e) => setRegEmail(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Contact Number"
                            fullWidth
                            value={regContact}
                            onChange={(e) => setRegContact(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Phone sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Password"
                            type={showPassword ? "text" : "password"}
                            fullWidth
                            value={regPassword}
                            onChange={(e) => setRegPassword(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowPassword(!showPassword)}
                                            edge="end"
                                        >
                                            {showPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <TextField
                            placeholder="Confirm Password"
                            type={showConfirmPassword ? "text" : "password"}
                            fullWidth
                            value={regConfirmPassword}
                            onChange={(e) => setRegConfirmPassword(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Lock sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton
                                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            edge="end"
                                        >
                                            {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Registering...
                                </>
                            ) : (
                                "SIGN UP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Typography variant="body2" sx={{ color: "#666" }}>
                                Already have an account?{" "}
                                <Link
                                    component="button"
                                    type="button"
                                    onClick={() => setCurrentView("login")}
                                    sx={{ 
                                        textDecoration: "none",
                                        color: "#667eea",
                                        fontWeight: 600,
                                        "&:hover": { textDecoration: "underline" }
                                    }}
                                >
                                    LOGIN
                                </Link>
                            </Typography>
                        </Box>
                    </form>
                )}

                {currentView === "register" && showOtpInput && (
                    <form onSubmit={handleVerifyOtp}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666", textAlign: "center" }}>
                            An OTP has been sent to {otpEmail}
                        </Typography>

                        <TextField
                            placeholder="Enter OTP"
                            fullWidth
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Verifying...
                                </>
                            ) : (
                                "Verify OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setShowOtpInput(false);
                                    setCurrentView("login");
                                }}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}

                {currentView === "forgot" && !showResetOtpInput && (
                    <form onSubmit={handleForgotPassword}>
                        <TextField
                            placeholder="Enter your email address"
                            type="email"
                            fullWidth
                            value={resetEmail}
                            onChange={(e) => setResetEmail(e.target.value)}
                            sx={{ mb: 3 }}
                            disabled={loading}
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <Email sx={{ color: "#999" }} />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    Sending OTP...
                                </>
                            ) : (
                                "Send OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => setCurrentView("login")}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}

                {currentView === "forgot" && showResetOtpInput && (
                    <form onSubmit={handleResetPassword}>
                        <Typography variant="body2" sx={{ mb: 2, color: "#666", textAlign: "center" }}>
                            Enter the OTP sent to {resetEmail}
                        </Typography>

                        <TextField
                            placeholder="Enter OTP"
                            fullWidth
                            value={resetOtp}
                            onChange={(e) => setResetOtp(e.target.value)}
                            sx={{ mb: 2 }}
                            disabled={loading}
                        />

                        {showPasswordFields && (
                            <>
                                <TextField
                                    placeholder="New Password"
                                    type={showPassword ? "text" : "password"}
                                    fullWidth
                                    value={newPassword}
                                    onChange={(e) => setNewPassword(e.target.value)}
                                    sx={{ mb: 2 }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: "#999" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowPassword(!showPassword)}
                                                    edge="end"
                                                >
                                                    {showPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />

                                <TextField
                                    placeholder="Confirm New Password"
                                    type={showConfirmPassword ? "text" : "password"}
                                    fullWidth
                                    value={confirmNewPassword}
                                    onChange={(e) => setConfirmNewPassword(e.target.value)}
                                    sx={{ mb: 3 }}
                                    disabled={loading}
                                    InputProps={{
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                <Lock sx={{ color: "#999" }} />
                                            </InputAdornment>
                                        ),
                                        endAdornment: (
                                            <InputAdornment position="end">
                                                <IconButton
                                                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                                    edge="end"
                                                >
                                                    {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                                                </IconButton>
                                            </InputAdornment>
                                        ),
                                    }}
                                />
                            </>
                        )}

                        <Button
                            type="submit"
                            variant="contained"
                            fullWidth
                            disabled={loading}
                            sx={{
                                py: 1.5,
                                fontSize: "1rem",
                                textTransform: "none",
                                background: "linear-gradient(90deg, #667eea 0%, #764ba2 100%)",
                                borderRadius: 25,
                                mb: 3,
                                "&:hover": {
                                    background: "linear-gradient(90deg, #5568d3 0%, #653a8b 100%)",
                                },
                            }}
                        >
                            {loading ? (
                                <>
                                    <CircularProgress size={20} sx={{ mr: 1, color: "#fff" }} />
                                    {showPasswordFields ? "Resetting..." : "Verifying..."}
                                </>
                            ) : (
                                showPasswordFields ? "Reset Password" : "Verify OTP"
                            )}
                        </Button>

                        <Box sx={{ textAlign: "center" }}>
                            <Link
                                component="button"
                                type="button"
                                onClick={() => {
                                    setShowResetOtpInput(false);
                                    setShowPasswordFields(false);
                                    setCurrentView("login");
                                }}
                                sx={{ 
                                    textDecoration: "none",
                                    color: "#667eea",
                                    "&:hover": { textDecoration: "underline" }
                                }}
                            >
                                Back to Login
                            </Link>
                        </Box>
                    </form>
                )}
            </Card>
        </Box>
    );
};

export default VendorLogin;